﻿import type { Promotion, PromotionUpsertInput, PromotionWithTargets } from '../domain/promotionTypes';
import {
  buildPromotionFromInput,
  buildTargetsFromInput,
  recordPromotionMutation,
  resolveOrganizationId,
  buildPromotionSyncPayload,
  summarizePromotionForAudit,
} from '../infra/promotionsRepoShared';
import { getPromotionByIdCache, listPromotionsCache } from '../infra/promotionsRepoRead';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { OfflineWriteBlockedError } from '../../shell/feature-flags/webOnlineWrites';
import { syncCatalogSnapshotFromRemote, upsertWebCatalogPromotionInCache } from '../../catalog/infra-api';
import type { WebCatalogPromotionRow, WebCatalogPromotionTargetRow } from '../../catalog/infra-api';
import { extractPromotionRemoteCode, mapPromotionRemoteError } from './mapPromotionRemoteError';

function toWebCatalogPromotionRow(promotion: Promotion): WebCatalogPromotionRow {
  return {
    id: promotion.id,
    organization_id: promotion.organizationId,
    name: promotion.name,
    description: promotion.description ?? null,
    kind: promotion.kind,
    scope: promotion.scope,
    value_percent: promotion.valuePercent ?? null,
    value_fixed: promotion.valueFixed ?? null,
    combo_buy_qty: promotion.comboBuyQty ?? null,
    combo_pay_qty: promotion.comboPayQty ?? null,
    combo_bundle_price: promotion.comboBundlePrice ?? null,
    starts_at: promotion.startsAt ?? null,
    ends_at: promotion.endsAt ?? null,
    is_active: promotion.isActive,
    branch_ids: promotion.branchIds ?? null,
    apply_mode: promotion.applyMode,
    promo_code: promotion.promoCode ?? null,
    priority: promotion.priority,
    stackable: promotion.stackable,
    channels: promotion.channels ?? null,
    conditions_json: promotion.conditions ?? null,
    created_at: promotion.createdAt ?? null,
    updated_at: promotion.updatedAt ?? new Date().toISOString(),
    deleted_at: promotion.deletedAt ?? null,
  };
}

function toWebCatalogPromotionTargetRows(
  targets: PromotionWithTargets['targets'],
): WebCatalogPromotionTargetRow[] {
  return targets.map((t) => ({
    id: t.id,
    promotion_id: t.promotionId,
    target_type: t.targetType,
    target_id: t.targetId,
    created_at: t.createdAt ?? null,
  }));
}

function mirrorPromotionInWebCache(
  promotion: Promotion,
  targets: PromotionWithTargets['targets'],
): void {
  upsertWebCatalogPromotionInCache(
    toWebCatalogPromotionRow(promotion),
    toWebCatalogPromotionTargetRows(targets),
  );
}

/**
 * Verifica que no exista otra promo con el mismo promo_code (case-insensitive).
 * La DB tiene unique index en (organization_id, lower(trim(promo_code))), pero
 * el error del server es genérico. Este pre-check da un mensaje claro al usuario.
 */
async function assertPromoCodeAvailable(promoCode: string | null | undefined, excludeId?: string): Promise<void> {
  const code = promoCode?.trim();
  if (!code) return;
  const upper = code.toUpperCase();
  const existing = await listPromotionsCache({ includeInactive: true });
  const duplicate = existing.find(
    (p) => p.id !== excludeId && p.promoCode?.trim().toUpperCase() === upper,
  );
  if (duplicate) {
    throw new Error(`Ya existe una promo con el código "${code}" (${duplicate.name}).`);
  }
}

/**
 * El RPC devuelve el updated_at server-side pero el pipeline de sync lo
 * descarta; lectura puntual para que el espejo RAM quede con timestamp real.
 * Best-effort: si falla, el espejo queda con el valor local y el pull lo cura.
 */
async function readRemotePromotionUpdatedAt(promotionId: string): Promise<string | null> {
  try {
    const { supabase } = await import('../../shared/supabase/supabaseClient');
    const { data, error } = await supabase
      .schema('pos')
      .from('promotions')
      .select('updated_at')
      .eq('id', promotionId)
      .maybeSingle();
    if (error || !data?.updated_at) return null;
    return String(data.updated_at);
  } catch {
    return null;
  }
}

async function submitPromotionCommand(params: {
  operation: 'insert' | 'update' | 'delete';
  promotion: Promotion;
  targets: PromotionWithTargets['targets'];
  failMessage: string;
  expectedUpdatedAt?: string | null;
}): Promise<{ appliedUpdatedAt: string | null }> {
  const { submitCommand } = await import('../../sync/infra-api');
  const organizationId = params.promotion.organizationId || (await resolveOrganizationId());

  let rawFailure: string | null = null;
  try {
    const result = await submitCommand({
      entity: 'promotion',
      operation: params.operation,
      payload: buildPromotionSyncPayload(
        params.promotion,
        params.targets,
        organizationId,
        params.operation,
        params.expectedUpdatedAt
      ),
    });
    if (!result.applied) {
      rawFailure = result.ingestError?.trim() || params.failMessage;
    }
  } catch (e: unknown) {
    if (e instanceof OfflineWriteBlockedError) throw e;
    rawFailure = e instanceof Error && e.message.trim() ? e.message.trim() : params.failMessage;
  }

  if (rawFailure) {
    const code = extractPromotionRemoteCode(rawFailure);
    const error = new Error(mapPromotionRemoteError(rawFailure));
    captureOpsFailure({
      module: 'promotions',
      op: 'promotions.write',
      error,
      ingestError: rawFailure,
      applied: false,
      path: 'web-ram',
      itemId: params.promotion.id,
      extra: {
        promotionId: params.promotion.id,
        operation: params.operation,
        ...(code ? { code } : {}),
      },
    });
    throw error;
  }

  // Post-effect best-effort (§4.1): falló el pull ≠ falló el guardado.
  try {
    await syncCatalogSnapshotFromRemote({ force: true, forceFull: true });
  } catch (err) {
    console.warn('[promotionIntake] pull post-write', err);
    captureOpsFailure({
      module: 'promotions',
      op: 'promotions.catalog_pull_post_write',
      error: err instanceof Error ? err : new Error(String(err)),
      applied: true,
      path: 'web-ram',
      itemId: params.promotion.id,
      extra: { promotionId: params.promotion.id, operation: params.operation },
    });
    const { showInfo } = await import('../../shared/ui/appToast');
    showInfo(
      'Promo guardada',
      'No se pudo refrescar el catálogo. Usá Actualizar en el listado si no la ves al toque.',
    );
  }

  return { appliedUpdatedAt: await readRemotePromotionUpdatedAt(params.promotion.id) };
}

export async function createPromotionIntake(input: PromotionUpsertInput): Promise<PromotionWithTargets> {
  const organizationId = await resolveOrganizationId();
  const promotion = buildPromotionFromInput(organizationId, input);
  const targets = buildTargetsFromInput(promotion.id, input);
  await assertPromoCodeAvailable(promotion.promoCode);

  const { appliedUpdatedAt } = await submitPromotionCommand({
          operation: 'insert',
          promotion,
          targets,
          failMessage: 'No se pudo crear la promoción en el servidor. Reintentá.',
        });
        // Post-write: el pull puede llegar stale; el listado lee RAM.
        // El updated_at remoto deja el eco del lock listo para el próximo edit.
        mirrorPromotionInWebCache(
          appliedUpdatedAt ? { ...promotion, updatedAt: appliedUpdatedAt } : promotion,
          targets,
        );
        await recordPromotionMutation({
          eventType: 'create',
          auditEventType: 'promotions.promotion.create',
          promotion,
          targets,
          summary: `Promoción creada: ${promotion.name}`,
        });
        return { ...promotion, targets };
}

export async function updatePromotionIntake(
  promotionId: string,
  input: PromotionUpsertInput,
): Promise<PromotionWithTargets> {
  const existing = await getPromotionByIdCache(promotionId);
  if (!existing) throw new Error('Promo no encontrada.');

  const promotion = buildPromotionFromInput(existing.organizationId, input, existing);
  const targets = buildTargetsFromInput(promotion.id, input, existing.targets);
  await assertPromoCodeAvailable(promotion.promoCode, promotionId);
  const before = summarizePromotionForAudit(existing, existing.targets);
  const wasActive = existing.isActive;
  const willBeActive = promotion.isActive;

  const { appliedUpdatedAt } = await submitPromotionCommand({
          operation: 'update',
          promotion,
          targets,
          failMessage: 'No se pudo actualizar la promoción en el servidor. Reintentá.',
          expectedUpdatedAt: existing.updatedAt ?? null,
        });
        // Full upsert (name/flags/…) — no solo is_active — para que el listado no quede stale.
        mirrorPromotionInWebCache(
          appliedUpdatedAt ? { ...promotion, updatedAt: appliedUpdatedAt } : promotion,
          targets,
        );
        if (wasActive !== willBeActive) {
          await recordPromotionMutation({
            eventType: willBeActive ? 'activate' : 'deactivate',
            auditEventType: willBeActive ? 'promotions.promotion.activate' : 'promotions.promotion.deactivate',
            promotion,
            targets,
            summary: willBeActive
              ? `Promoción activada: ${promotion.name}`
              : `Promoción desactivada: ${promotion.name}`,
          });
        } else {
          await recordPromotionMutation({
            eventType: 'update',
            auditEventType: 'promotions.promotion.update',
            promotion,
            targets,
            summary: `Promoción actualizada: ${promotion.name}`,
            before,
          });
        }
        return { ...promotion, targets };
}

export async function deletePromotionIntake(promotionId: string): Promise<void> {
  const existing = await getPromotionByIdCache(promotionId);
  if (!existing) return;

  const deletedAt = new Date().toISOString();
  const promotion: Promotion = {
    ...existing,
    isActive: false,
    deletedAt,
    updatedAt: deletedAt,
  };

  const { appliedUpdatedAt } = await submitPromotionCommand({
    operation: 'delete',
    promotion,
    targets: existing.targets,
    failMessage: 'No se pudo eliminar la promoción en el servidor. Reintentá.',
    expectedUpdatedAt: existing.updatedAt ?? null,
  });
  // Post-write: marcar deleted_at en el cache RAM — el listado filtra
  // deleted_at, así la promo desaparece sin esperar el pull remoto.
  mirrorPromotionInWebCache(
    appliedUpdatedAt ? { ...promotion, updatedAt: appliedUpdatedAt } : promotion,
    existing.targets,
  );
  await recordPromotionMutation({
    eventType: 'delete',
    auditEventType: 'promotions.promotion.delete',
    promotion,
    targets: existing.targets,
    summary: `Promoción eliminada: ${promotion.name}`,
  });
}
